import React from 'react'

import {
  AwardCard,
  ExpandedAwardCard,
  Overlay,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { DefaultError, Transition } from 'src/chrome'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { AwardFragment, getAwards } from 'src/graphql'
import { useQueries, useState } from 'src/hooks'

import { IconType } from '@sendoutcards/quantum-design-ui/dist/atoms/iconography/types'
import { css } from 'src/styled'

const styles = {
  awardsPage: css({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    minWidth: '320px',
    alignItems: 'flex-start',
    padding: '0px 32px 32px 32px',
  }),
  headingContainer: css({
    width: `100%`,
    maxWidth: '1300px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  }),
  gridContainer: css({
    width: '100%',
    maxWidth: '1300px',
    alignItems: 'left',
  }),
  grid: css({
    display: 'grid',
    justifyItems: 'center',
    alignItems: 'center',
    maxWidth: '100%',
    gridGap: '24px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gridAutoRows: 'minmax(285px, auto)',
    gridAutoFlow: 'dense',
  }),
}

const progressPercentage = ({ progress, threshold }: AwardFragment) => {
  if (progress <= 0) return 0
  if (progress > threshold) return 100
  return Math.floor((progress / threshold) * 100)
}

const Awards: React.FC<{}> = () => {
  const [awards] = useQueries(getAwards())
  const [expandedAward, setExpandedAward] = useState<AwardFragment>()

  return (
    <div>
      <div css={styles.awardsPage}>
        <div css={styles.headingContainer}>
          <Text type={'title'} content="My Awards" />
        </div>
        <div css={styles.gridContainer}>
          <div css={styles.grid}>
            {awards.map((award: AwardFragment, j: number) => {
              return (
                <div key={'AwardCard' + j} css={{ flexBasis: '16.666667%' }}>
                  <AwardCard
                    key={award.title}
                    title={award.title}
                    image={award.iconUrl}
                    progress={
                      !award.incrementalProgress
                        ? progressPercentage(award)
                        : {
                            ...award.incrementalProgress,
                            icon: award.incrementalProgress.icon as IconType,
                          }
                    }
                    onClick={() => setExpandedAward(award)}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>
      {expandedAward && (
        <Overlay>
          <ExpandedAwardCard
            image={
              progressPercentage(expandedAward) === 100
                ? expandedAward.graphicUrl
                : expandedAward.iconUrl
            }
            title={expandedAward.title}
            description={
              progressPercentage(expandedAward) === 100
                ? expandedAward.completedDescription
                : expandedAward.description
            }
            checklist={expandedAward.checklist}
            close={() => setExpandedAward(undefined)}
            progress={
              !expandedAward.incrementalProgress
                ? progressPercentage(expandedAward)
                : {
                    ...expandedAward.incrementalProgress,
                    icon: expandedAward.incrementalProgress.icon as IconType,
                  }
            }
            checklistTitle={expandedAward.checklistTitle}
            checklistSubtitle={expandedAward.checklistSubtitle}
          />
        </Overlay>
      )}
    </div>
  )
}

export default suspenseBoundary({
  component: Awards,
  unresolved: (
    <Transition message={'Loading Awards...'} style={{ position: 'static' }} />
  ),
  failure: DefaultError,
})
